import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, LogOut, Zap, MapPin, Inbox, TrendingUp, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { startSocialPulseLogin } from "@/lib/oidc";
import { partnerMe, partnerUpdateLead } from "@/lib/api";

const LEAD_STATUSES = ["new", "contacted", "qualified", "won", "lost"];

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function KeralaPartnerPortal() {
  const { user, login } = useAuth();
  useEffect(() => { document.title = "Kerala.com · Partner Portal"; }, []);
  if (user === undefined) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin text-emerald-700" size={26} /></div>;
  if (!user) return <PartnerAuth login={login} />;
  return <PartnerDashboard />;
}

function PartnerAuth({ login }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const inp = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 outline-none focus:border-emerald-600 text-sm";
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(form.email, form.password); toast.success("Welcome, partner"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
    setLoading(false);
  };
  return (
    <section className="bg-gradient-to-br from-[#024E3F] to-[#012a22] min-h-[80vh] py-16 flex items-center" data-testid="partner-portal-auth">
      <div className="max-w-md mx-auto px-4 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl soft-shadow-lg p-8">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Partner Login</h1>
          <p className="text-slate-500 text-sm mt-1">Approved Kerala.com partners — log in to work your assigned leads.</p>
          <form onSubmit={submit} className="space-y-3 mt-6">
            <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="partner-login-email" required />
            <input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} data-testid="partner-login-password" required />
            <button type="submit" disabled={loading} data-testid="partner-login-submit" className="w-full bg-emerald-700 text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Log In <ArrowRight size={16} /></>}
            </button>
          </form>
          <div className="flex items-center gap-3 my-5"><span className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">or</span><span className="h-px bg-slate-200 flex-1" /></div>
          <button type="button" onClick={async () => { try { await startSocialPulseLogin(); } catch { toast.error("Could not start SocialPulse."); } }}
            data-testid="partner-sso" className="relative w-full rounded-lg p-[2px] bg-gradient-to-r from-[#A855F7] to-[#4ADE80] hover:shadow-[0_0_18px_rgba(168,85,247,0.28)] transition-shadow group">
            <span className="flex items-center justify-center gap-2 w-full py-3 rounded-[7px] bg-white text-slate-800 font-semibold group-hover:bg-slate-50"><Zap size={16} className="text-[#A855F7]" /> Continue with SocialPulse</span>
          </button>
          <p className="text-xs text-slate-400 mt-4 text-center">Not a partner yet? <a href="/kerala/partners" className="text-emerald-700 font-semibold">Apply here</a>.</p>
        </motion.div>
      </div>
    </section>
  );
}

function PartnerDashboard() {
  const { logout } = useAuth();
  const [data, setData] = useState(undefined); // undefined=loading, null=not a partner
  const load = useCallback(() => {
    partnerMe().then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await partnerUpdateLead(id, { status }); setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? { ...l, status } : l)) })); }
    catch { toast.error("Update failed"); }
  };

  if (data === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-emerald-700" size={24} /></div>;
  if (data === null) return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="partner-not-approved">
      <div className="text-center max-w-md">
        <MapPin className="text-emerald-700 mx-auto" size={34} />
        <h2 className="font-heading text-xl font-bold text-slate-900 mt-3">No partner account linked</h2>
        <p className="text-slate-500 mt-2">This login isn't linked to an approved Kerala.com partner yet. If you've applied, we'll email you once approved.</p>
        <div className="mt-5 flex gap-3 justify-center">
          <a href="/kerala/partners" className="bg-emerald-700 text-white font-semibold rounded-xl px-5 py-2.5">Apply to be a partner</a>
          <button onClick={logout} className="btn-outline rounded-xl px-5 py-2.5">Log out</button>
        </div>
      </div>
    </div>
  );

  const { partner, leads, stats } = data;
  return (
    <div className="bg-slate-50 min-h-[80vh]" data-testid="partner-dashboard">
      <div className="bg-[#024E3F] text-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold">{partner.business_name || partner.name}</h1>
            <p className="text-emerald-100/70 text-sm"><MapPin size={12} className="inline -mt-0.5" /> {[partner.city, partner.district, partner.state].filter(Boolean).join(", ")} · Kerala.com Partner</p>
          </div>
          <button onClick={logout} data-testid="partner-logout" className="ml-auto text-xs bg-white/10 border border-white/20 rounded-lg px-3 py-2 hover:bg-white/20 inline-flex items-center gap-1.5"><LogOut size={13} /> Log out</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[[Inbox, "Total leads", stats.total], [TrendingUp, "New", stats.new], [Trophy, "Won", stats.won]].map(([Icon, l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow">
              <Icon size={18} className="text-emerald-700" />
              <p className="font-heading text-2xl font-extrabold text-slate-900 mt-2">{v ?? 0}</p>
              <p className="text-xs uppercase tracking-wide text-slate-400">{l}</p>
            </div>
          ))}
        </div>

        <h2 className="font-heading font-bold text-slate-900 mb-3">Your lead inbox</h2>
        {!leads.length ? (
          <p className="text-slate-500 text-center py-12 bg-white border border-slate-200 rounded-2xl">No leads yet. Leads from your city will appear here automatically.</p>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-shadow overflow-x-auto" data-testid="partner-leads">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Interest</th><th className="text-left p-3">When</th><th className="text-left p-3">Status</th></tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100" data-testid="partner-lead-row">
                    <td className="p-3 font-semibold text-slate-800">{l.name}<div className="text-xs text-slate-400 font-normal">{l.business_name}</div></td>
                    <td className="p-3 text-slate-600">{l.email}<div className="text-xs text-slate-400">{l.phone}</div></td>
                    <td className="p-3 text-slate-600">{l.package_interest || l.service_interest || l.vertical || "—"}</td>
                    <td className="p-3 text-slate-400 text-xs">{(l.created_at || "").slice(0, 10)}</td>
                    <td className="p-3">
                      <select value={l.status || "new"} onChange={(e) => setStatus(l.id, e.target.value)} data-testid="partner-lead-status"
                        className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 outline-none">
                        {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
